import { ActionCreatorWithPayload } from '@reduxjs/toolkit'
import { catchErrorByStatus, handleError } from 'helpers/errors'
import { PromiseResponse } from 'helpers/types'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { CREATOR_ACCOMMODATION_STEPS, STEPS } from 'routes/steps'
import {
    AccommodationEditPoint,
    removeAccommodationFromStore,
} from 'store/accommodation-slice'
import {
    useGetCartItemAccommodationQuery,
    useDeleteCartItemsByIdMutation,
    useGetCartQuery,
} from 'store/api-slice/hooks'
import { CartCreatorType } from 'store/api-slice/types'
import { removeCabanas } from 'store/cabana-slice'
import { setIsErrorModalOpened } from 'store/general-slice'
import {
    CreatorAccommodationStep,
    GlobalStep,
    SelfcareStep,
    Step,
    UnlimitedCardStep,
    UnlimitedRenewStep,
    VouchersStep,
} from 'store/general-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { LanguageType } from 'store/steps'
import { removeAllTickets, removeFamilyPackage } from 'store/ticket-slice'
import { removeTransport } from 'store/transport-slice'
import { setAccommodationDetails, setCart, setIsFetchingLocked } from '.'
import { getLanguageFromLocale } from 'helpers/i18n'

export const useCart = (): {
    isCartEmpty: boolean
    isStepEmpty: (
        stepId:
            | Step
            | CreatorAccommodationStep
            | SelfcareStep
            | GlobalStep
            | UnlimitedCardStep
            | UnlimitedRenewStep
            | VouchersStep
    ) => boolean
    isCartFetching: boolean
    isSummaryFetching: boolean
    cartCreatorType: CartCreatorType
    refetchCart: (canUnlockFetching?: boolean) => void
    removeNextStepsFromCart: ({
        activeStepId,
    }: {
        activeStepId?: Step | CreatorAccommodationStep | 'cart'
    }) => Promise<PromiseResponse>
} => {
    // <-- !!! do not change to { language } = useApp() -> infinite loop errors !!!
    const { locale } = useIntl()
    const language: LanguageType = getLanguageFromLocale(locale)
    // -->
    const dispatch = useAppDispatch()

    const [deleteCartItems, deleteCartItemsStatus] =
        useDeleteCartItemsByIdMutation()
    const { isLoading: isCartItemsProcessing, isError: isCartItemsError } =
        deleteCartItemsStatus

    const {
        tickets,
        ticketsBundles,
        ticketsCityBreak,
        marchVouchers,
        spa,
        busTickets,
        accommodation,
        cabanas,
        cards,
        vouchers,
    } = useAppSelector((state) => state.cart.items)
    const { cart, isFetchingLocked } = useAppSelector((state) => state.cart)
    const { vouchersBoxes } = useAppSelector((state) => state.vouchers)
    const { appMode, isAppAccessible } = useAppSelector(
        (state) => state.general
    )

    let cartCreatorType: CartCreatorType = 'standard'
    switch (appMode) {
        case 'unlimited-card':
            cartCreatorType = 'card'
            break
        case 'unlimited-renew':
            cartCreatorType = 'card-renew'
            break
        case 'creator-accommodation':
            cartCreatorType = 'accommodation'
            break
        case 'vouchers':
            cartCreatorType = 'voucher'
            break
        default:
            cartCreatorType = 'standard'
            break
    }

    const {
        data: cartData,
        refetch,
        isFetching: isCartFetching,
        error: isCartError,
    } = useGetCartQuery(
        {
            _locale: language,
            cartCreatorType,
        },
        {
            skip:
                (appMode !== 'creator' &&
                    appMode !== 'creator-accommodation' &&
                    appMode !== 'unlimited-card' &&
                    appMode !== 'unlimited-renew' &&
                    appMode !== 'vouchers') ||
                !isAppAccessible ||
                isFetchingLocked,
        }
    )

    const { data: accommodationDetails, isFetching: isAccommodationFetching } =
        useGetCartItemAccommodationQuery(
            {
                _locale: language,
                cartItemId: accommodation?.cartItemId || '',
            },
            { skip: !accommodation?.cartItemId }
        )

    // <-- !!! do not change to { isCreatorAccommodation } = useApp() -> infinite loop errors !!!
    const isCreatorAccommodation = appMode === 'creator-accommodation'
    // -->
    const isSummaryFetching =
        isCartFetching || isAccommodationFetching || isCartItemsProcessing

    useEffect(() => {
        if (isCartFetching) return

        if (isCartError) {
            // show 'something went wrong' only when error occurs after previous success fetching
            cart?.cartId && dispatch(setIsErrorModalOpened(true))
            return
        }

        if (cartData)
            dispatch(
                setCart({
                    ...cartData,
                    appMode,
                    itemsSequence:
                        // set items fetched from cart in the same order as items in redux
                        appMode === 'vouchers'
                            ? {
                                  vouchers: vouchersBoxes.map(
                                      (voucher) => voucher.cartItemId || ''
                                  ),
                              }
                            : undefined,
                })
            )
    }, [isCartFetching])

    useEffect(() => {
        if (isAccommodationFetching || !accommodationDetails?.items) return

        dispatch(setAccommodationDetails(accommodationDetails.items))
    }, [isAccommodationFetching])

    useEffect(() => {
        // catch global errors
        // TODO: check if it is possible to put this into the the rtk middleware
        isCartItemsError && dispatch(setIsErrorModalOpened(true))
    }, [isCartItemsError])

    const isStepEmpty = (
        stepId:
            | Step
            | CreatorAccommodationStep
            | SelfcareStep
            | GlobalStep
            | UnlimitedCardStep
            | UnlimitedRenewStep
            | VouchersStep
    ) => {
        if (!cart?.cartId) return true
        switch (stepId) {
            case 'tickets':
                return (
                    !tickets.length &&
                    !ticketsBundles.length &&
                    !ticketsCityBreak.length
                )
            case 'upsell':
                return !(spa.length || busTickets.length || cabanas.length)
            case 'accommodation':
            case 'accommodation_details':
                return (accommodation?.quantity || 0) === 0
            case 'card_summary':
            case 'renew_summary':
                return cards.length === 0
            case 'vouchers_order':
                return vouchers.length === 0
            default:
                return true
        }
    }

    const isCartEmpty =
        isStepEmpty('tickets') &&
        isStepEmpty('upsell') &&
        isStepEmpty('accommodation') &&
        isStepEmpty('card_summary') &&
        isStepEmpty('renew_summary') &&
        isStepEmpty('vouchers_order')

    const refetchCart = (shouldUnlockFetching = false) => {
        if (isFetchingLocked) {
            shouldUnlockFetching && dispatch(setIsFetchingLocked(false))
            return
        }
        refetch()
    }

    // TODO: consider unifying with clearing items from cart when skipping step (SkipStepModal.tsx)
    const removeNextStepsFromCart = async ({
        activeStepId = 'cart',
    }: {
        activeStepId?: Step | CreatorAccommodationStep | 'cart'
    }) => {
        const steps = isCreatorAccommodation
            ? CREATOR_ACCOMMODATION_STEPS
            : STEPS
        const stepsIds = steps.map((step) => step.id)
        let cartSteps = [
            'cart',
            ...stepsIds.filter(
                (step) => step !== 'accommodation_details' && step !== 'order'
            ),
        ]
        const activeStepIndex = cartSteps.findIndex(
            (step) => step === activeStepId
        )

        if (activeStepIndex === -1) return { isSuccess: false }

        const cartItemIdsToDelete: string[] = []
        const stepActionsAfterDelete: ActionCreatorWithPayload<
            AccommodationEditPoint,
            string
        >[] = []

        cartSteps = cartSteps.splice(activeStepIndex + 1)
        cartSteps.forEach((step) => {
            switch (step) {
                case 'tickets': {
                    tickets.map(
                        ({ cartItemId }) =>
                            cartItemId && cartItemIdsToDelete.push(cartItemId)
                    )
                    ticketsBundles
                        .map(({ itemsBundle }) => itemsBundle || [])
                        .flat()
                        .map(
                            ({ cartItemId }) =>
                                cartItemId &&
                                cartItemIdsToDelete.push(cartItemId)
                        )
                    marchVouchers.map(
                        ({ cartItemId }) =>
                            cartItemId && cartItemIdsToDelete.push(cartItemId)
                    )
                    // for accommodation creator
                    ticketsCityBreak.map(
                        ({ cartItemId }) =>
                            cartItemId && cartItemIdsToDelete.push(cartItemId)
                    )

                    stepActionsAfterDelete.push(removeAllTickets)
                    stepActionsAfterDelete.push(removeFamilyPackage)
                    break
                }
                case 'upsell': {
                    ;[...spa, ...busTickets, ...cabanas].map(
                        ({ cartItemId }) =>
                            cartItemId && cartItemIdsToDelete.push(cartItemId)
                    )
                    stepActionsAfterDelete.push(removeTransport, removeCabanas)
                    break
                }
                case 'accommodation':
                    accommodation?.cartItemId &&
                        cartItemIdsToDelete.push(accommodation?.cartItemId)
                    // for standard creator
                    ticketsCityBreak.map(
                        ({ cartItemId }) =>
                            cartItemId && cartItemIdsToDelete.push(cartItemId)
                    )
                    stepActionsAfterDelete.push(removeAccommodationFromStore)
                    break
                default:
                    break
            }
        })

        if (!cartItemIdsToDelete.length) return { isSuccess: true }

        try {
            handleError(
                await deleteCartItems({
                    _locale: language,
                    cartCreatorType,
                    deleteCartItemsByIdCommand: {
                        cartItemUuids: cartItemIdsToDelete,
                    },
                })
            )
        } catch (errors: unknown) {
            // try to reckognize one of 'global' errors, otherwise just reset session
            catchErrorByStatus({ error: errors as MessageErrorModel }) ||
                dispatch(setIsErrorModalOpened(true))
            throw errors
        }

        refetchCart()

        stepActionsAfterDelete.forEach((action) => {
            dispatch(action(undefined))
        })

        return { isSuccess: true }
    }

    return {
        isCartEmpty,
        isStepEmpty,
        isCartFetching,
        isSummaryFetching,
        cartCreatorType,
        refetchCart,
        removeNextStepsFromCart,
    }
}
